import React from 'react';
import { USER_TYPES } from '../../../utils/constants';

import AuditTrailItem from '../../themed/audit-trail-item';
import Loader from '../../themed/loader';
import Conditional from '../../common/conditional';

import classNames from './style.module.scss';

import * as authService from '../../../services/authService';
import * as reportService from '../../../services/reportService';

import * as utils from '../../../utils/utilities';
import * as models from '../../../utils/models';
import { useLocation } from 'react-router-dom';

interface AuditTrailProps {
	reportId: string;
}

interface Audit extends models.Report {
	acceptedBySignature: string;
	approvedComment: string;
	reviews: models.Review[];
	rejectedBy: boolean;
	rejectedByName: string;
	rejectedDate: string;
	rejectedComment: string;
	rejectedByDesignation: string;
	rejectedBySignature: string;
	viewerName: string;
	viewedAt: string;
}

const AuditTrail: React.FC<AuditTrailProps> = ({ reportId }) => {
	const [loading, setLoading] = React.useState(false);
	const [auditTrail, setAuditTrail] = React.useState<Audit>();

	const location = useLocation();
	
	const getCanSeeWhenCustomerViewed = (): boolean => {
		const user = authService.getCurrentUser() as models.User;

		const isInternalUser: boolean = user.userType === USER_TYPES.INTERNAL;
		const isViewed = auditTrail?.viewed as boolean;

		return isInternalUser && isViewed;
	};

	React.useEffect(() => {
		const getAuditTrail = async () => {
			if (!reportId) return;

			try {
				setLoading(true);

				const result = await reportService.getReport(reportId);
				setAuditTrail(result.data);

				setLoading(false);
			} catch (ex: any) {
				setLoading(false);

				const cachedData = ex.response.data;
				if (cachedData) setAuditTrail(cachedData);
			}
		};

		getAuditTrail();
	}, [reportId]);

	React.useLayoutEffect(() => {
		if (location.hash) {
			const element = document.querySelector(location.hash);
			if (element) element.scrollIntoView({ behavior: 'smooth' });
		}
	}, [location, auditTrail]);

	if (!auditTrail || typeof auditTrail === 'string') {
		return null;
	}

	return (
		<React.Fragment>
			<Loader isVisible={loading} />

			<Conditional isVisible={!loading}>
				<div className="my-4 divider"></div>

				<div className={classNames.auditTrail}>
					<div className={classNames.auditTrailHeading}>
						<i className="bi bi-file-text-fill"></i> Audit Trail
					</div>

					<div className={classNames.auditTrailItem}>
						<AuditTrailItem label="Created By" value={auditTrail.creatorName} />

						<AuditTrailItem
							label="Signature"
							signature={auditTrail.createdBySignature}
						/>

						<AuditTrailItem
							label="Date Created"
							value={utils.formatTime(auditTrail.createdAt)}
						/>
						
						<AuditTrailItem
							label="Last Updated"
							value={utils.formatTime(auditTrail.updatedAt)}
						/>
					</div>

					<Conditional isVisible={getCanSeeWhenCustomerViewed()}>
						<div className={classNames.auditTrailItem}>
							<AuditTrailItem
								label="Viewed By Contact"
								value={auditTrail.viewerName}
							/>

							<AuditTrailItem
								label="Viewed At"
								value={utils.formatTimeWithRelativeStamp(auditTrail.viewedAt)}
							/>
						</div>
					</Conditional>

					<Conditional isVisible={auditTrail.rejectedBy}>
						<div className={classNames.auditTrailItem}>
							<AuditTrailItem
								label="Rejected By"
								value={auditTrail.rejectedByName}
							/>

							<AuditTrailItem
								label="Date"
								value={utils.formatTime(auditTrail.rejectedDate)}
							/>

							<AuditTrailItem
								label="Comment"
								value={auditTrail.rejectedComment}
							/>

							<AuditTrailItem
								label="Signature"
								signature={auditTrail.rejectedBySignature}
							/>
						</div>
					</Conditional>

					<Conditional isVisible={auditTrail.approved}>
						<div className={classNames.auditTrailItem}>
							<AuditTrailItem
								label="Approved By"
								value={auditTrail.approvedByName}
							/>

							<AuditTrailItem
								label="Position"
								value={auditTrail.approvedByDesignation}
							/>

							<AuditTrailItem
								label="Comment"
								value={auditTrail.approvedComment}
							/>

							<AuditTrailItem
								label="Date"
								value={utils.formatTime(auditTrail.approvedDate)}
							/>

							<AuditTrailItem
								label="Signature"
								signature={auditTrail.approvedBySignature}
							/>
						</div>
					</Conditional>

					<Conditional isVisible={auditTrail.isCustomerReviewed}>
						<div className={classNames.auditTrailItem}>
							<AuditTrailItem
								label="Reviewed By Customer"
								value={auditTrail.customerReviewerName}
							/>

							<AuditTrailItem
								label="Position"
								value={auditTrail.customerReviewerDesignation}
							/>

							<AuditTrailItem
								label="Status"
								value={auditTrail.customerReviewStatus}
							/>

							<AuditTrailItem
								label="Comment"
								value={auditTrail.customerReviewComment}
							/>

							<AuditTrailItem
								label="Date"
								value={utils.formatTime(auditTrail.customerReviewerDate)}
							/>

							<AuditTrailItem
								label="Signature"
								signature={auditTrail.customerReviewerSignature}
							/>
						</div>
					</Conditional>

					<Conditional isVisible={auditTrail.accepted}>
						<div className={classNames.auditTrailItem}>
							<AuditTrailItem
								label="Accepted By"
								value={auditTrail.acceptedByName}
							/>

							<AuditTrailItem
								label="Date"
								value={utils.formatTime(auditTrail.acceptedAt)}
							/>

							<AuditTrailItem
								label="Signature"
								signature={auditTrail.acceptedBySignature}
							/>
						</div>
					</Conditional>
				</div>

				<Conditional isVisible={auditTrail.reviews?.length ? auditTrail.reviews.length > 0 : false}>
					<div className="divider my-4"></div>

					<div className={classNames.auditTrail}>
						<div className={classNames.auditTrailHeading}>
							<i className="bi bi-clock-history"></i> Review Timeline
						</div>

						{auditTrail.reviews?.map((review) => (
							<div key={review._id} className={classNames.auditTrailItem}>
								<AuditTrailItem
									label="Reviewed By"
									value={review.reviewerName}
								/>

								<AuditTrailItem
									label="Date"
									value={utils.formatTime(review.createdAt)}
								/>

								<AuditTrailItem
									label="Position"
									value={review.reviewerDesignation}
								/>

								<AuditTrailItem label="Comment" value={review.reviewComment} />

								<AuditTrailItem label="Status" value={review.reviewStatus} />

								<AuditTrailItem
									label="Signature"
									signature={review.reviewerSignature}
								/>
							</div>
						))}
					</div>
				</Conditional>

				<div id="audit-trail" className="my-4 divider"></div>
			</Conditional>
		</React.Fragment>
	)
};

export default AuditTrail;
